import type { ApplicationRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ConsentService } from '../consent/consent.service';

const SCRIPT_MARKER = 'data-pickapeak-gtag';

/**
 * GA4: load `gtag/js` only when analytics consent is granted; sync consent on CMP changes; SPA page_view on router.
 */
export function initializeGoogleAnalyticsWithConsent(applicationRef: ApplicationRef, measurementId: string): void {
  const router = applicationRef.injector.get(Router);
  const consent = applicationRef.injector.get(ConsentService);

  let routerSub: Subscription | null = null;
  let gtagBootstrapped = false;

  const sendPageView = (path: string): void => {
    const gtag = window.gtag;
    if (typeof gtag !== 'function') {
      return;
    }
    gtag('event', 'page_view', {
      page_path: path,
      page_location: `${window.location.origin}${path}`,
      page_title: document.title,
    });
  };

  const bindRouter = (): void => {
    if (routerSub) {
      return;
    }
    sendPageView(router.url);
    routerSub = router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => sendPageView(e.urlAfterRedirects));
  };

  const unbindRouter = (): void => {
    routerSub?.unsubscribe();
    routerSub = null;
  };

  const run = (): void => {
    if (!consent.analyticsAllowed()) {
      if (gtagBootstrapped && window.gtag) {
        window.gtag('consent', 'update', { analytics_storage: 'denied', ad_storage: 'denied' });
      }
      unbindRouter();
      return;
    }

    window.dataLayer = window.dataLayer ?? [];
    window.gtag =
      window.gtag ??
      ((...args: unknown[]) => {
        window.dataLayer.push(args);
      });

    window.gtag('consent', 'update', { analytics_storage: 'granted', ad_storage: 'denied' });

    if (!document.querySelector(`script[${SCRIPT_MARKER}]`)) {
      window.gtag('js', new Date());
      window.gtag('config', measurementId, { send_page_view: false });
      const script = document.createElement('script');
      script.setAttribute(SCRIPT_MARKER, measurementId);
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.head.appendChild(script);
    }

    gtagBootstrapped = true;
    bindRouter();
  };

  run();
  consent.changed$.subscribe(() => run());
}
